import { BlastAtmosphere } from '../components/BlastAtmosphere'
import { EmptyState, ErrorState, LoadingState } from '../components/Feedback'
import { PlayerAvatar } from '../components/PlayerAvatar'
import { api, ApiError } from '../lib/api'
import { number } from '../lib/format'
import { Link, useParams } from '../lib/router'
import { useApi } from '../lib/useApi'
import {
  tournamentPlacementLabel,
  tournamentStateLabel,
} from '../lib/tournamentLabels'

export function TournamentDetailPage() {
  const { id = '' } = useParams()
  const detail = useApi(`tournament-${id}`, (signal) =>
    api.tournament(id, signal),
  )
  const bracket = useApi(`bracket-${id}`, (signal) => api.bracket(id, signal))

  if (detail.loading) {
    return (
      <main className="section page">
        <LoadingState label="Caricamento torneo" />
      </main>
    )
  }
  if (detail.error) {
    const missing =
      detail.error instanceof ApiError && detail.error.status === 404
    return (
      <main className="section page">
        {missing ? (
          <EmptyState
            message="L'identificatore richiesto non corrisponde a un torneo registrato."
            title="Torneo non trovato"
          />
        ) : (
          <ErrorState />
        )}
      </main>
    )
  }
  if (!detail.data) return null
  const tournament = detail.data.data

  return (
    <main className="section page tournament-detail page--charged page--tournaments">
      <BlastAtmosphere variant="subtle" />
      <header className="page-heading page-heading--split">
        <div>
          <p className="eyebrow">
	    Eliminazione diretta · {tournament.mode}
	  </p>

	  <h1>{tournament.displayName}</h1>

	  <p className="tournament-technical-id">
	    ID torneo: {tournament.id}
	  </p>
          <p>
            {tournament.participantCount}/{tournament.size} partecipanti ·
            seeding {tournament.seeding.toUpperCase()}
          </p>
        </div>
	<span className={`status-chip status-chip--${tournament.status}`}>
	  {tournamentStateLabel(tournament.status)}
	</span>
      </header>
      
      {tournament.winner ? (
        <section className="winner-banner">
          <PlayerAvatar
            size={72}
            username={tournament.winner.username}
            uuid={tournament.winner.uuid}
          />
          <div>
            <span className="eyebrow">Campione</span>
            <Link to={`/player/${tournament.winner.uuid}`}>
              {tournament.winner.username}
            </Link>
          </div>
        </section>
      ) : null}

      <section>
        <header className="section-heading">
          <p className="eyebrow">Percorso single elimination</p>
          <h2>Bracket</h2>
        </header>
        {bracket.loading ? <LoadingState label="Costruzione bracket" /> : null}
        {bracket.error ? (
          <ErrorState
            message="Il torneo è disponibile, ma il bracket non può essere caricato."
            title="Bracket non disponibile"
          />
        ) : null}
        {!bracket.loading &&
        !bracket.error &&
        bracket.data?.data.rounds.length === 0 ? (
          <EmptyState
            message="Il bracket verrà generato quando le iscrizioni saranno complete."
            title="Bracket in attesa"
          />
        ) : null}
        <div className="bracket">
          {bracket.data?.data.rounds.map((round) => (
            <section className="bracket-round" key={round.roundNumber}>
              <header>
                <span>Round {round.roundNumber}</span>
                <h3>{round.name}</h3>
              </header>
              <div className="bracket-round__matches">
                {round.matches.map((match) => (
                  <article className="bracket-match" key={match.id}>
                    <span className="bracket-match__status">{tournamentStateLabel(match.status)}</span>
                    {[match.playerA, match.playerB].map((player, index) =>
                      player ? (
                        <Link
                          className={
                            match.winner?.uuid === player.uuid ? 'winner' : ''
                          }
                          key={player.uuid}
                          to={`/player/${player.uuid}`}
                        >
                          <span>#{player.seedNumber ?? '—'}</span>
                          <strong>{player.username}</strong>
                        </Link>
                      ) : (
                        <div className="bye" key={`bye-${index}`}>
                          <span>—</span>
                          <strong>BYE</strong>
                        </div>
                      ),
                    )}
                  </article>
                ))}
              </div>
            </section>
          ))}
        </div>
      </section>

      <section>
        <header className="section-heading">
          <p className="eyebrow">Seeding iniziale</p>
          <h2>Partecipanti</h2>
        </header>
        <div className="participants-grid">
          {tournament.participants.map((participant) => (
            <Link
              className="participant-card"
              key={participant.uuid}
              to={`/player/${participant.uuid}`}
            >
              <PlayerAvatar
                size={48}
                username={participant.username}
                uuid={participant.uuid}
              />
              <div>
                <strong>{participant.username}</strong>
                <span>{number(participant.seedMmr)} seed MMR</span>
                <span>
		  {tournamentStateLabel(participant.status)}
		  {participant.finalPlacement
		    ? ` · ${tournamentPlacementLabel(participant.finalPlacement)}`
		    : ''}
		</span>
              </div>
              <span>#{participant.seedNumber ?? '—'}</span>
            </Link>
          ))}
        </div>
      </section>
    </main>
  )
}
